import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { formatDateDMY } from '../utils/dateFormat';

/**
 * AttendanceReport — admin grid mirroring the user's monthly timesheet PDF.
 *
 * Three side-panels:
 *   • Holidays calendar (add / delete public holidays).
 *   • Pending app punches (approve / reject so they count as Present).
 *   • Machine XLSX import (parses the biometric export and seeds machine
 *     punches per user per day).
 *
 * The grid itself: rows = users (excluding super_admin), columns = days of
 * the chosen month. Each cell shows the resolved code (P / H / A / V / M
 * / WFH / —). Admin can click any cell to override the code.
 */
const CODES = ['P', 'A', 'V', 'M', 'WFH', 'H'];
const CODE_COLOR = {
  P: 'bg-emerald-100 text-emerald-800',
  A: 'bg-rose-100 text-rose-800',
  V: 'bg-violet-100 text-violet-800',
  M: 'bg-amber-100 text-amber-800',
  WFH: 'bg-blue-100 text-blue-800',
  H: 'bg-slate-200 text-slate-700',
  '—': 'bg-white text-slate-300',
};

const monthName = (m) => new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' });

const AttendanceReport = () => {
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'attendance_report', 'edit');
  const canCreate = hasPermission(user, 'attendance_report', 'create');
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // {user_id, name, date, code}
  const fileRef = useRef(null);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/attendance/report?year=${year}&month=${month}`);
      setReport(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadHolidays = async () => {
    const res = await api.get('/attendance/holidays');
    setHolidays(res.data?.items || []);
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/attendance/pending-approvals');
      setPending(res.data?.items || []);
    } catch (e) {
      setPending([]);
    }
  };

  useEffect(() => { loadReport(); }, [year, month]); // eslint-disable-line
  useEffect(() => { loadHolidays(); loadPending(); }, []); // eslint-disable-line

  /* ── Holidays ──────────────────────────────────────────────────── */
  const [newHolDate, setNewHolDate] = useState('');
  const [newHolLabel, setNewHolLabel] = useState('');
  const addHoliday = async () => {
    if (!newHolDate || !newHolLabel.trim()) return;
    await api.post('/attendance/holidays', { holiday_date: newHolDate, label: newHolLabel.trim() });
    setNewHolDate(''); setNewHolLabel('');
    await loadHolidays();
    await loadReport();
  };
  const deleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    await api.delete(`/attendance/holidays/${id}`);
    await loadHolidays();
    await loadReport();
  };

  /* ── Pending approvals ────────────────────────────────────────── */
  const approvePunch = async (id) => {
    await api.post(`/attendance/punch/${id}/approve`);
    await loadPending();
    await loadReport();
  };
  const rejectPunch = async (id) => {
    const reason = window.prompt('Reason for rejection (3+ chars):', '');
    if (!reason || reason.trim().length < 3) return;
    await api.post(`/attendance/punch/${id}/reject?reason=${encodeURIComponent(reason.trim())}`);
    await loadPending();
    await loadReport();
  };

  /* ── Manual cell override ─────────────────────────────────────── */
  const saveMark = async (code) => {
    if (!editingCell) return;
    if (code === 'CLEAR') {
      await api.delete(`/attendance/mark?user_id=${editingCell.user_id}&mark_date=${editingCell.date}`);
    } else {
      await api.post('/attendance/mark', {
        user_id: editingCell.user_id,
        mark_date: editingCell.date,
        code,
      });
    }
    setEditingCell(null);
    await loadReport();
  };

  /* ── Machine import ───────────────────────────────────────────── */
  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post('/attendance/import-machine', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      await loadReport();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* ── Excel export — mirrors the user's PDF grid ───────────────── */
  const exportExcel = () => {
    if (!report) return;
    const wb = XLSX.utils.book_new();
    const head = ['S#', 'EMP NO', 'NAME OF EMPLOYEES',
      ...report.days.map((d) => `${d.date.slice(8)} ${d.weekday}`),
      'TOTAL'];
    const rows = report.rows.map((r, i) => [
      i + 1,
      r.emp_code || '',
      r.name || r.email || '',
      ...r.cells.map((c) => c.code),
      r.total_present_days,
    ]);
    // Legend rows
    rows.push([]);
    rows.push(['Legend:', 'P=Present', 'H=Holiday/Weekend', 'A=Absent', 'V=Vacation', 'M=Medical', 'WFH=Work From Home']);
    const ws = XLSX.utils.aoa_to_sheet([head, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, `${monthName(month)} ${year}`);
    XLSX.writeFile(wb, `Attendance_${year}_${String(month).padStart(2, '0')}.xlsx`);
  };

  /* ── Render ───────────────────────────────────────────────────── */
  const years = useMemo(() => {
    const cur = today.getFullYear();
    return [cur - 1, cur, cur + 1];
  }, []); // eslint-disable-line

  return (
    <div className="px-4 py-5 space-y-4" data-testid="attendance-report-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">🗓️ Attendance Report</h1>
          <p className="text-xs text-slate-500">Reconciles biometric machine + in-app punches into one monthly grid.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="border rounded px-2 py-1.5 text-sm" data-testid="att-year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="border rounded px-2 py-1.5 text-sm" data-testid="att-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{monthName(m)}</option>
            ))}
          </select>
          <button onClick={exportExcel} className="px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm" data-testid="att-export-xlsx">
            ⬇️ Excel
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white border rounded-lg overflow-auto" data-testid="att-grid-wrap">
        {loading && <div className="p-6 text-center text-slate-500">Loading…</div>}
        {!loading && report && (
          <table className="min-w-full text-[11px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-slate-700">S#</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700">EMP NO</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-700 sticky left-0 bg-slate-50">NAME</th>
                {report.days.map((d) => (
                  <th key={d.date} className={`px-1 py-1 text-center font-semibold ${d.is_weekend ? 'bg-slate-200' : ''}`} title={d.date}>
                    <div className="text-[10px] text-slate-600">{d.weekday}</div>
                    <div className="text-slate-800">{d.date.slice(8)}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-semibold text-slate-700">TOTAL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.rows.map((r, idx) => (
                <tr key={r.user_id} className="hover:bg-blue-50/30" data-testid={`att-row-${r.user_id}`}>
                  <td className="px-2 py-1.5 text-slate-500">{idx + 1}</td>
                  <td className="px-2 py-1.5 text-slate-600">{r.emp_code || '—'}</td>
                  <td className="px-2 py-1.5 font-medium text-slate-900 sticky left-0 bg-white">{r.name || r.email}</td>
                  {r.cells.map((c) => {
                    const punchSummary = c.punches?.length
                      ? c.punches.map((p) => `${p.action.toUpperCase()} ${new Date(p.punch_time).toLocaleTimeString()} (${p.source}${p.approval_status !== 'approved' ? ' — ' + p.approval_status : ''})`).join('\n')
                      : '';
                    const title = [
                      c.holiday_label ? `Holiday: ${c.holiday_label}` : null,
                      c.mark_note ? `Note: ${c.mark_note}` : null,
                      punchSummary,
                    ].filter(Boolean).join('\n');
                    return (
                      <td key={c.date} className="p-0.5 text-center">
                        <button
                          onClick={() => canEdit && setEditingCell({ user_id: r.user_id, name: r.name, date: c.date, code: c.code })}
                          title={title}
                          className={`block w-full text-center text-[11px] font-bold py-1 rounded ${CODE_COLOR[c.code] || ''} ${canEdit ? 'cursor-pointer hover:ring-1 hover:ring-blue-400' : 'cursor-default'}`}
                          data-testid={`att-cell-${r.user_id}-${c.date}`}
                        >
                          {c.code}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-center font-semibold text-slate-900">{r.total_present_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && report && report.rows.length === 0 && (
          <div className="p-6 text-center text-slate-500">No users found.</div>
        )}
      </div>

      {/* Legend */}
      {report && (
        <div className="text-[11px] text-slate-600 flex flex-wrap gap-3">
          {Object.entries(report.legend).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded font-bold ${CODE_COLOR[k] || ''}`}>{k}</span>
              <span>{v}</span>
            </span>
          ))}
        </div>
      )}

      {/* Side panels: pending, holidays, import */}
      <div className="grid md:grid-cols-3 gap-4 pt-2">
        {/* Pending */}
        <div className="bg-white border rounded-lg p-3" data-testid="att-pending-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm text-slate-800">🟠 Pending App Punches ({pending.length})</div>
          </div>
          {pending.length === 0 && <div className="text-xs text-slate-500">All app punches reviewed.</div>}
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {pending.map((p) => (
              <div key={p.id} className="border rounded p-2 text-xs" data-testid={`pending-punch-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900">
                    {p.user_name} <span className="text-slate-500 font-normal">· {p.action.toUpperCase()}</span>
                  </div>
                  <span className="text-[10px] text-slate-500">{formatDateDMY(p.punch_date)}</span>
                </div>
                <div className="text-slate-600 mt-0.5">{new Date(p.punch_time).toLocaleTimeString()}</div>
                {p.address && <div className="text-slate-600 truncate" title={p.address}>📍 {p.address}</div>}
                {p.reason && <div className="text-slate-500 italic mt-0.5">&ldquo;{p.reason}&rdquo;</div>}
                {canEdit && (
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={() => approvePunch(p.id)} className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 text-white" data-testid={`approve-punch-${p.id}`}>Approve</button>
                    <button onClick={() => rejectPunch(p.id)} className="text-[11px] px-2 py-0.5 rounded border border-rose-600 text-rose-700" data-testid={`reject-punch-${p.id}`}>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Holidays */}
        <div className="bg-white border rounded-lg p-3" data-testid="att-holidays-panel">
          <div className="font-semibold text-sm text-slate-800 mb-2">🎌 Public Holidays</div>
          {canCreate && (
            <div className="flex gap-1 mb-2">
              <input type="date" value={newHolDate} onChange={(e) => setNewHolDate(e.target.value)} className="border rounded px-1.5 py-1 text-xs flex-1" data-testid="new-holiday-date" />
              <input type="text" placeholder="Label" value={newHolLabel} onChange={(e) => setNewHolLabel(e.target.value)} className="border rounded px-1.5 py-1 text-xs flex-1" data-testid="new-holiday-label" />
              <button onClick={addHoliday} className="px-2 py-1 rounded bg-blue-600 text-white text-xs" data-testid="add-holiday-btn">+</button>
            </div>
          )}
          <div className="space-y-1 max-h-72 overflow-y-auto text-xs">
            {holidays.length === 0 && <div className="text-slate-500">No holidays yet.</div>}
            {holidays.map((h) => (
              <div key={h.id} className="flex items-center justify-between border rounded px-2 py-1">
                <div>
                  <div className="font-medium text-slate-900">{formatDateDMY(h.holiday_date)}</div>
                  <div className="text-slate-600">{h.label}</div>
                </div>
                {hasPermission(user, 'attendance_report', 'delete') && (
                  <button onClick={() => deleteHoliday(h.id)} className="text-rose-600 text-xs">✕</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Machine import */}
        <div className="bg-white border rounded-lg p-3" data-testid="att-import-panel">
          <div className="font-semibold text-sm text-slate-800 mb-2">📥 Import Machine Report</div>
          <p className="text-xs text-slate-600 mb-2">Upload the biometric machine&apos;s monthly XLSX export. Existing machine punches for the same days are replaced.</p>
          {canCreate && (
            <input ref={fileRef} type="file" accept=".xlsx" onChange={handleImport} className="text-xs" disabled={importing} data-testid="import-machine-xlsx" />
          )}
          {importing && <div className="text-xs text-slate-500 mt-2">Importing…</div>}
          {importResult && (
            <div className="text-xs mt-2 space-y-1">
              {importResult.error ? (
                <div className="text-rose-700">⚠️ {importResult.error}</div>
              ) : (
                <>
                  <div className="text-emerald-700">✅ Inserted {importResult.punches_inserted} punches.</div>
                  <div className="text-slate-600">Matched: {importResult.matched_employees?.join(', ') || '—'}</div>
                  {importResult.unmatched_employees?.length > 0 && (
                    <div className="text-amber-700">
                      ⚠️ Unmatched: {importResult.unmatched_employees.map((u) => u.name).join(', ')}
                      <div className="text-[10px] text-slate-500">Tip: edit the employee&apos;s name in CRM Users to match the machine export, then re-import.</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cell override modal */}
      {editingCell && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditingCell(null)} data-testid="mark-override-modal">
          <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="font-semibold text-slate-900">Override attendance</div>
              <div className="text-xs text-slate-500">{editingCell.name} · {formatDateDMY(editingCell.date)}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {CODES.map((c) => (
                <button key={c} onClick={() => saveMark(c)} className={`py-2 rounded text-sm font-bold ${CODE_COLOR[c]} hover:ring-2 hover:ring-blue-400`} data-testid={`mark-${c}`}>
                  {c}
                </button>
              ))}
            </div>
            <button onClick={() => saveMark('CLEAR')} className="w-full py-1.5 rounded border text-xs text-slate-700 hover:bg-slate-50" data-testid="mark-clear">
              ↺ Clear override
            </button>
            <button onClick={() => setEditingCell(null)} className="w-full py-1.5 rounded bg-slate-800 text-white text-sm" data-testid="mark-close">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceReport;
